import { Component } from '@angular/core';
import { FormControl, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { SubmissionService } from 'src/app/services/reporting/submission.service';
import { SharedMethodsService } from 'src/app/services/shared-methods.service';
import { UserStorageService } from 'src/app/services/storage/user-storage.service';
import { CancelReport } from '../../data-models/report-models/cancel-report.model';
import { Trip } from '../../data-models/trip-models/trip.model';
import { UserData } from '../../data-models/user-models/user-data.model';
import { AuthenticationService } from '../../services/authentication.service';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-cancel-report',
    templateUrl: './cancel-report.component.html',
    styleUrls: ['./cancel-report.component.css'],
    standalone: false
})
export class CancelReportComponent {

  isProduction: boolean = environment.production;

  isReportSending: boolean = false; // flag that tells us if the report is sending (so we can show the loading screen)

  // data preambles
  userDetails: UserData = new UserData();
  currTrip: Trip = new Trip();
  cancelReport: CancelReport = new CancelReport();

  cancelForm: UntypedFormGroup = new UntypedFormGroup({
    comments: new FormControl<string>('') // optional
  });

  // page to page
  numPages: number = 0;
  currPage: number = 0;
  spinnerProgress: number = 0;
  spinnerIncrement: number = 0; 

  constructor(
    private router: Router,
    public submissionService: SubmissionService,
    private authenticationService: AuthenticationService,
    public sharedMethodsService: SharedMethodsService,
  ) {

    this.authenticationService.getUserDetails().then(() => {
      this.userDetails = UserStorageService.currUser;
    }).then(() => {
      UserStorageService.getCurrTrip().then(() => {
        if (this.authenticationService.dataError(UserStorageService.error)) {
          return;
        }
        this.currTrip = UserStorageService.currTrip;
        this.initialize();
      });
    });

  }


  /*  ---------- Report Initialization Methods  ---------- */


  // initialize values
  initialize(): void {
    
    this.numPages = 1;
    this.spinnerIncrement = 100 / this.numPages;
    this.toNextPage();
   
  }


  /* ---------- Page Navigation Methods ---------- */


  toNextPage(): void {

    if (this.currPage > this.numPages) {
      this.submitReport();
      return;
    }

    this.currPage++;
    this.spinnerProgress += this.spinnerIncrement;

  }

  toPreviousPage(): void {

    if (this.currPage === 1) {
      this.sharedMethodsService.toHome(this.userDetails.currHomepage);
      return;
    }
    this.currPage--;
    this.spinnerProgress -= this.spinnerIncrement;

  }


  /* ---------- Report Completion Methods ---------- */
  
  
  // initiates the report saving/sending process
  submitReport(): void {

    this.isReportSending = true; // set flag
    this.saveReportData();
    this.attemptTransmission(); // attempt to send the report to DFO

  }

  // saves report data to the user's pouch, then initiates the sending process
  saveReportData(): void {

    this.cancelReport.completionDate = new Date(); // get current date/time
    this.cancelReport.submissionDate = new Date();
    this.cancelReport.comments = this.cancelForm.get('comments')?.value.trim();
    this.cancelReport.isComplete = true; // set submission flag
    this.cancelReport.reportID = SharedMethodsService.generateReportID("C");
    this.currTrip.cancelFishingReport = this.cancelReport; // copy over the report

    // save current trip and set homepage to no-trip
    this.userDetails.currHomepage = 'no-trip-home';

  }

  // composes the email body and subject, then invokes a method to email the report to DFO
  attemptTransmission(): void {
    UserStorageService.saveUser(this.userDetails).then(() => {
      UserStorageService.saveTrip(this.currTrip).then(() => {
        // change false to !isProduction to enable mocking the submission
        if (false) { // mock the submission process if the app is in development mode
          this.sharedMethodsService.toastEmailSent();
          this.cancelReport.confirmationNumber = "FOS 12345";
          this.cancelReport.isSubmitted = true;
          UserStorageService.saveTrip(this.currTrip).then(() => this.router.navigate(['submission-page'], { state: { name: 'Cancel Fishing Report', success: this.cancelReport.isSubmitted }, replaceUrl: true }));
        } else {
          this.submissionService.sendReportData(this.userDetails.userEmail, this.cancelReport.reportID, this.currTrip.tripID).then((success: boolean) => {
            if (success) { // if the email was sent successfully
              this.sharedMethodsService.toastEmailSent();
              this.cancelReport.isSubmitted = true; // set the report submissionflag
              this.cancelReport.confirmationNumber = "Awaiting response";
              UserStorageService.saveTrip(this.currTrip).then(() => this.router.navigate(['submission-page'], { state: { name: 'Cancel Fishing Report', success: this.cancelReport.isSubmitted }, replaceUrl: true }));
            } else {
              this.sharedMethodsService.toastUnableToSubmitReport();
              this.currTrip.unsubmittedReports.push(this.cancelReport.completionDate);
              UserStorageService.saveTrip(this.currTrip).then(() => this.router.navigate(['submission-page'], { state: { name: 'Cancel Fishing Report', success: this.cancelReport.isSubmitted }, replaceUrl: true }));
            }
          });
        }
      });
    });
    
  }

}
